import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, BookOpen, Loader2, Search } from "lucide-react";

type Outcome = "succeeded" | "partial" | "paused" | "failed" | "ongoing";

type CaseStudyRow = {
  project_id: string;
  idea_id: string;
  idea_title: string;
  idea_description: string | null;
  idea_category: string | null;
  idea_image_url: string | null;
  author_display_name: string | null;
  latest_outcome: Outcome;
  latest_summary: string;
  latest_lesson_at: string;
  public_lessons_count: number;
  tags: string[] | null;
};

type CaseStudyLesson = {
  id: string;
  outcome: Outcome;
  summary: string;
  what_worked: string | null;
  what_didnt_work: string | null;
  what_id_do_differently: string | null;
  advice_for_others: string | null;
  tags: string[];
  created_at: string;
};

type CaseStudyDetail = {
  project_id: string;
  idea_id: string;
  idea_title: string;
  idea_description: string | null;
  idea_category: string | null;
  idea_image_url: string | null;
  idea_tags: string[] | null;
  author_display_name: string | null;
  lessons: CaseStudyLesson[];
};

const OUTCOME_META: Record<Outcome, { label: string; cls: string }> = {
  succeeded: { label: "Succeeded", cls: "bg-primary/15 text-primary" },
  partial: { label: "Partial win", cls: "bg-amber-500/15 text-amber-500" },
  paused: { label: "Paused", cls: "bg-muted text-muted-foreground" },
  failed: { label: "Failed", cls: "bg-destructive/15 text-destructive" },
  ongoing: { label: "Ongoing", cls: "bg-secondary text-secondary-foreground" },
};

const OutcomeBadge = ({ outcome }: { outcome: Outcome }) => {
  const meta = OUTCOME_META[outcome];
  return (
    <span
      className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full ${meta.cls}`}
    >
      {meta.label}
    </span>
  );
};

const CaseStudiesIndex = () => {
  const [q, setQ] = useState("");
  const [outcome, setOutcome] = useState<Outcome | "all">("all");

  const { data, isLoading } = useQuery({
    queryKey: ["case-studies-index"],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_public_case_studies");
      if (error) throw error;
      return (data ?? []) as CaseStudyRow[];
    },
  });

  const filtered = useMemo(() => {
    if (!data) return [];
    const needle = q.trim().toLowerCase();
    return data.filter((r) => {
      if (outcome !== "all" && r.latest_outcome !== outcome) return false;
      if (!needle) return true;
      const hay = [
        r.idea_title,
        r.idea_description ?? "",
        r.idea_category ?? "",
        r.author_display_name ?? "",
        r.latest_summary,
        ...(r.tags ?? []),
      ]
        .join(" ")
        .toLowerCase();
      return hay.includes(needle);
    });
  }, [data, q, outcome]);

  useEffect(() => {
    document.title = "Case Studies | Alabama Archive | emrkt.co";
  }, []);

  const outcomes: (Outcome | "all")[] = ["all", "succeeded", "partial", "ongoing", "paused", "failed"];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main className="pt-32 pb-20">
        <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
          <Link
            to="/archive"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
          >
            <ArrowLeft className="h-4 w-4" /> Back to Archive
          </Link>

          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-primary mb-2">
            <BookOpen className="h-3.5 w-3.5" /> Alabama Archive
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold mb-2">Case Studies</h1>
          <p className="text-sm text-muted-foreground max-w-2xl mb-6">
            Structured outcomes and lessons learned from Alabama projects. Success and failure are
            equally valuable — both feed the knowledge base.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <div className="relative flex-1">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder="Search case studies, communities, lessons…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
            <div className="flex flex-wrap gap-1">
              {outcomes.map((o) => (
                <Button
                  key={o}
                  size="sm"
                  variant={outcome === o ? "default" : "outline"}
                  onClick={() => setOutcome(o)}
                  className="capitalize"
                >
                  {o === "all" ? "All outcomes" : OUTCOME_META[o].label}
                </Button>
              ))}
            </div>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="border border-dashed border-border rounded-xl p-10 text-center text-sm text-muted-foreground">
              No case studies match your filters yet. As project owners publish public Lessons
              Learned, they'll appear here.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filtered.map((r) => (
                <Link
                  key={r.project_id}
                  to={`/archive/case-studies/${r.project_id}`}
                  className="group bg-card border border-border rounded-xl overflow-hidden hover:border-primary/40 transition-colors flex flex-col"
                >
                  {r.idea_image_url && (
                    <div className="aspect-[16/9] bg-muted overflow-hidden">
                      <img
                        src={r.idea_image_url}
                        alt={r.idea_title}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform"
                      />
                    </div>
                  )}
                  <div className="p-5 flex-1 flex flex-col">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <OutcomeBadge outcome={r.latest_outcome} />
                      {r.idea_category && (
                        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                          {r.idea_category}
                        </span>
                      )}
                      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                        {r.public_lessons_count} lesson{r.public_lessons_count === 1 ? "" : "s"}
                      </span>
                    </div>
                    <h3 className="font-display text-lg font-semibold leading-snug mb-1 group-hover:text-primary transition-colors">
                      {r.idea_title}
                    </h3>
                    <p className="text-sm text-muted-foreground line-clamp-3 mb-3">
                      {r.latest_summary}
                    </p>
                    <div className="mt-auto flex items-center justify-between text-[11px] text-muted-foreground">
                      <span>
                        {r.author_display_name ? `by ${r.author_display_name}` : "Alabama community member"}
                      </span>
                      <span>{new Date(r.latest_lesson_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
};

const CaseStudyDetailView = ({ projectId }: { projectId: string }) => {
  const { data, isLoading } = useQuery({
    queryKey: ["case-study", projectId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_public_case_study", {
        _project_id: projectId,
      });
      if (error) throw error;
      return ((data ?? []) as CaseStudyDetail[])[0] ?? null;
    },
  });

  useEffect(() => {
    if (data?.idea_title) {
      document.title = `${data.idea_title} | Case Study | emrkt.co`;
    }
  }, [data]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main className="pt-32 pb-20">
        <div className="container mx-auto px-4 sm:px-6 max-w-4xl">
          <Link
            to="/archive/case-studies"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
          >
            <ArrowLeft className="h-4 w-4" /> All case studies
          </Link>

          {isLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : !data ? (
            <div className="text-center py-12">
              <h1 className="font-display text-2xl font-bold mb-2">Case study not found</h1>
              <p className="text-sm text-muted-foreground">
                This project may not have any public lessons yet.
              </p>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-primary mb-2">
                <BookOpen className="h-3.5 w-3.5" /> Case Study
              </div>
              <h1 className="font-display text-3xl sm:text-4xl font-bold mb-2">{data.idea_title}</h1>
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground mb-4">
                {data.idea_category && <span>{data.idea_category}</span>}
                {data.author_display_name && <span>· by {data.author_display_name}</span>}
                <span>· {data.lessons.length} public lesson{data.lessons.length === 1 ? "" : "s"}</span>
              </div>
              {data.idea_image_url && (
                <div className="aspect-[16/9] bg-muted rounded-xl overflow-hidden mb-6">
                  <img
                    src={data.idea_image_url}
                    alt={data.idea_title}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              {data.idea_description && (
                <div className="prose prose-sm dark:prose-invert max-w-none mb-8">
                  <p className="text-foreground/90 whitespace-pre-wrap">{data.idea_description}</p>
                </div>
              )}

              <h2 className="font-display text-xl font-semibold mb-3">Lessons Learned</h2>
              <div className="space-y-4">
                {data.lessons.map((l) => (
                  <article
                    key={l.id}
                    className="bg-card border border-border rounded-xl p-5"
                  >
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <OutcomeBadge outcome={l.outcome} />
                      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                        {new Date(l.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-base font-semibold mb-3">{l.summary}</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                      {l.what_worked && (
                        <Field label="What worked" value={l.what_worked} />
                      )}
                      {l.what_didnt_work && (
                        <Field label="What didn't work" value={l.what_didnt_work} />
                      )}
                      {l.what_id_do_differently && (
                        <Field label="What I'd do differently" value={l.what_id_do_differently} />
                      )}
                      {l.advice_for_others && (
                        <Field label="Advice for others" value={l.advice_for_others} />
                      )}
                    </div>
                    {l.tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-3">
                        {l.tags.map((t) => (
                          <Badge key={t} variant="secondary" className="text-[10px]">
                            {t}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </article>
                ))}
              </div>

              <div className="mt-8 text-sm">
                <Link
                  to={`/ideas/${data.idea_id}`}
                  className="text-primary hover:underline"
                >
                  View the original idea →
                </Link>
              </div>
            </>
          )}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
};

const Field = ({ label, value }: { label: string; value: string }) => (
  <div>
    <div className="text-muted-foreground uppercase tracking-wide text-[10px] mb-1">{label}</div>
    <div className="text-foreground whitespace-pre-wrap">{value}</div>
  </div>
);

const CaseStudies = () => {
  const { projectId } = useParams<{ projectId: string }>();
  if (projectId) return <CaseStudyDetailView projectId={projectId} />;
  return <CaseStudiesIndex />;
};

export default CaseStudies;